import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Send, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader, Avatar } from "@/components/Brand";
import { api, errMsg, useData } from "@/lib/api";
import { fullName, fmtTime, personalize, TEMP_STYLE } from "@/lib/crm";
const PRESETS = [
  "Hi {{First Name}}, thanks for your interest in the AVY training session! When's a good time to chat?",
  "Hi {{First Name}}, just following up on your inquiry — happy to answer any questions.",
  "{{First Name}}, your spot is almost ready. Reply YES to confirm your session.",
];

export default function SMS() {
  const location = useLocation();
  const [contacts] = useData("/contacts", []);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const endRef = useRef(null);

  const active = contacts.find((c) => c.id === activeId);

  useEffect(() => {
    const cid = location.state?.contactId;
    if (cid) setActiveId(cid);
  }, [location.state]);

  useEffect(() => {
    if (!activeId && contacts.length) setActiveId(contacts[0].id);
  }, [contacts, activeId]);

  const load = async (id) => {
    try { const { data } = await api.get(`/sms?contact_id=${id}`); setMessages(data); }
    catch { setMessages([]); }
  };
  useEffect(() => { if (activeId) load(activeId); }, [activeId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/sms", { contact_id: active.id, body: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const list = contacts.filter((c) => fullName(c).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="SMS Messenger" subtitle="Text your leads from the workspace. Messages are logged to each lead's history (sandbox — no real texts are sent)." />
      <div className="grid gap-4 lg:grid-cols-[320px_1fr] rounded-2xl border bg-white overflow-hidden" style={{ height: "66vh" }}>
        <div className="border-r flex flex-col min-h-0">
          <div className="p-3 border-b">
            <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" className="pl-9 h-9" data-testid="sms-search-input" /></div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="sms-lead-list">
            {list.map((c) => (
              <button key={c.id} type="button" onClick={() => setActiveId(c.id)} className={`flex w-full items-center gap-3 border-b px-3 py-3 text-left transition-colors ${activeId === c.id ? "bg-[#F6F2FB]" : "hover:bg-stone-50"}`} data-testid={`sms-lead-${c.id}`}>
                <Avatar user={{ name: fullName(c) }} size={36} />
                <div className="min-w-0"><div className="font-semibold text-sm text-[#19082B] truncate">{fullName(c)}</div><div className="text-xs text-stone-500 truncate">{c.phone || c.email}</div></div>
              </button>
            ))}
            {!list.length && <div className="p-6 text-center text-sm text-stone-500">No leads found.</div>}
          </div>
        </div>

        <div className="flex flex-col min-h-0">
          {active ? (
            <>
              <div className="flex items-center gap-3 border-b px-4 py-3">
                <Avatar user={{ name: fullName(active) }} size={36} />
                <div><div className="font-semibold text-[#19082B] flex items-center gap-2" data-testid="sms-active-name">{fullName(active)}{active.temperature && <span className={`rounded-full border px-1.5 text-[9px] font-bold ${TEMP_STYLE[active.temperature]}`} data-testid="sms-temp">{active.temperature}</span>}</div><div className="text-xs text-stone-500">{active.phone || "No phone on file"}</div></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FBFAFE]" data-testid="sms-thread">
                {!messages.length && <div className="text-center text-sm text-stone-400 mt-6">No messages yet. Say hello 👋</div>}
                {messages.map((m) => (
                  <div key={m.id} className="flex justify-end" data-testid={`sms-message-${m.id}`}>
                    <div className="max-w-[75%] rounded-2xl rounded-br-sm bg-[#3B185F] px-3.5 py-2 text-sm text-white">
                      {m.body}
                      <div className="mt-0.5 text-[10px] text-white/60 text-right">{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="border-t p-3 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p, i) => <button key={i} type="button" onClick={() => setText(personalize(p, active))} className="rounded-full border px-2.5 py-1 text-xs text-[#3B185F] hover:bg-[#F6F2FB]" data-testid={`sms-preset-${i}`}>{personalize(p, active).slice(0, 34)}…</button>)}
                </div>
                <form onSubmit={send} className="flex items-center gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" maxLength={320} data-testid="sms-input" />
                  <span className="text-[10px] text-stone-400 w-10 text-right">{text.length}/320</span>
                  <Button type="submit" className="bg-[#D4AF37] text-[#19082B] hover:bg-[#C59B27]" data-testid="sms-send-btn"><Send className="h-4 w-4" /></Button>
                </form>
              </div>
            </>
          ) : <div className="grid place-items-center h-full text-stone-400">Select a lead to start messaging.</div>}
        </div>
      </div>
    </div>
  );
}
